import { NextRouter } from 'next/router';
import { useEffect } from 'react';
import { isBuildOutdated } from '../utils/buildVersion';
import { isNewApp } from '../utils/newAppShell';

const CHECK_EVERY_MS = 10 * 60 * 1000;
/** Away at least this long and outdated → reload on return; the user left, so nothing in progress is lost. */
const RELOAD_AFTER_AWAY_MS = 30 * 60 * 1000;

/**
 * Keeps an open app (the new Android app only, never the website) on the latest release. Pages are cached by the browser/app, so after a deploy a tab can
 * keep running the old build for days. We ask the server which build is current, and
 *  - on the user's next in-app navigation we do a full page load instead (nothing on screen is interrupted),
 *  - when the app is brought back after a long absence we reload right away.
 * Checks are cheap (a tiny no-store request), throttled, and only run while the page is visible.
 */
export const useAppUpdate = (router: Pick<NextRouter, 'events'>) => {
  useEffect(() => {
    // New Android app only: the website keeps its normal behaviour.
    if (process.env.NODE_ENV !== 'production' || !isNewApp()) return;

    let outdated = false;
    let lastCheck = 0;
    let hiddenAt = 0;

    const check = async () => {
      if (outdated || document.visibilityState !== 'visible' || Date.now() - lastCheck < 60 * 1000) return;
      lastCheck = Date.now();
      outdated = await isBuildOutdated();
    };

    const onVisibility = async () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      await check();
      if (outdated && hiddenAt && Date.now() - hiddenAt >= RELOAD_AFTER_AWAY_MS) window.location.reload();
    };

    const onRouteStart = (url: string) => {
      if (!outdated) return;
      // Stop the client-side transition and load the target as a fresh page from the newest build.
      window.location.assign(url);
      router.events.emit('routeChangeError');
      throw 'Aborted client route change: reloading for a new app version';
    };

    const first = window.setTimeout(check, 5000);
    const interval = window.setInterval(check, CHECK_EVERY_MS);
    document.addEventListener('visibilitychange', onVisibility);
    router.events.on('routeChangeStart', onRouteStart);
    return () => {
      window.clearTimeout(first);
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      router.events.off('routeChangeStart', onRouteStart);
    };
  }, [router.events]);
};
